import type { Dispatch } from '@reduxjs/toolkit'
import { AuthenticationStorage } from '@axilon-apps/domains-identity/shared/core'
import { refreshAuthenticationToken } from '@axilon-apps/domains-identity/state'

import { getOntologyApiBaseUrl } from '../../config'
import { SSERequestError, consumeSSE } from '../../agents/sse-client'

const INITIAL_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000
const BACKOFF_JITTER = 0.2
/** Two missed 15 s heartbeats plus margin. */
export const DOMAIN_EVENTS_STALL_MS = 35_000

export const DOMAIN_EVENT_TOPICS = [
  'jobs',
  'runs',
  'agent-writes',
  'merges',
  'tasks',
  'chat-queue'
] as const
export type DomainEventTopic = (typeof DOMAIN_EVENT_TOPICS)[number]
export type DomainEventsStatus = 'connecting' | 'live' | 'degraded' | 'off'

export interface SubscribeDomainEventsOptions {
  topics: readonly DomainEventTopic[]
  onReady: (granted: readonly DomainEventTopic[]) => void
  onSignal: (topic: DomainEventTopic) => void
  /** A gap the client can't scope to one topic: refetch every topic. */
  onResyncAll: () => void
  onStatus: (status: DomainEventsStatus) => void
  /** Refreshes the identity token, which stream requests can't get from the RTK base query. */
  dispatch: Dispatch
  signal: AbortSignal
}

type DomainEventFrame =
  | { type: 'ready'; topics: DomainEventTopic[] }
  | { type: 'signal'; topic: DomainEventTopic }
  | { type: 'heartbeat' }
  | { type: 'unknown' }

/** Exponential backoff capped at 30 s, with ±20% jitter so tabs don't reconnect together. */
export function nextDomainEventsBackoffMs(
  attempt: number,
  random: () => number = Math.random
): number {
  const base = Math.min(INITIAL_BACKOFF_MS * 2 ** Math.max(0, attempt), MAX_BACKOFF_MS)
  return Math.round(base * (1 + BACKOFF_JITTER * (2 * random() - 1)))
}

export function parseDomainEventFrame(event: unknown): DomainEventFrame {
  if (!event || typeof event !== 'object') return { type: 'unknown' }
  const frame = event as { type?: unknown; topic?: unknown; topics?: unknown }
  switch (frame.type) {
    case 'ready':
      return {
        type: 'ready',
        topics: Array.isArray(frame.topics) ? frame.topics.filter(isDomainEventTopic) : []
      }
    case 'signal':
      return isDomainEventTopic(frame.topic)
        ? { type: 'signal', topic: frame.topic }
        : { type: 'unknown' }
    case 'heartbeat':
      return { type: 'heartbeat' }
    default:
      return { type: 'unknown' }
  }
}

function isDomainEventTopic(value: unknown): value is DomainEventTopic {
  return (DOMAIN_EVENT_TOPICS as readonly unknown[]).includes(value)
}

function isAuthFailure(error: unknown): boolean {
  return error instanceof SSERequestError && (error.status === 401 || error.status === 403)
}

/**
 * Reconnecting wake-up stream for organization-wide domain changes. Frames carry
 * no data or entity IDs, so callers refetch whole topics. Every failed attempt
 * calls `onResyncAll` once; while the stream is down that loop is the only refresh.
 * Like the RTK base query, it refreshes a token that is about to expire before
 * each attempt, and answers a 401 with one forced refresh and an immediate retry.
 */
export async function subscribeDomainEvents({
  topics,
  onReady,
  onSignal,
  onResyncAll,
  onStatus,
  dispatch,
  signal
}: SubscribeDomainEventsOptions): Promise<void> {
  let attempt = 0
  let refreshedAfterUnauthorized = false
  onStatus('connecting')
  while (!signal.aborted) {
    const refresh = await refreshAuthenticationToken(dispatch)
    if (signal.aborted) return
    if (!refresh.ok) {
      onStatus('off')
      return
    }

    const attemptController = new AbortController()
    const abortAttempt = () => attemptController.abort()
    signal.addEventListener('abort', abortAttempt, { once: true })
    let ready = false
    let stalled = false
    let watchdog: ReturnType<typeof setTimeout> | undefined
    const armWatchdog = () => {
      clearTimeout(watchdog)
      watchdog = setTimeout(() => {
        stalled = true
        attemptController.abort()
      }, DOMAIN_EVENTS_STALL_MS)
    }

    let failure: unknown
    try {
      armWatchdog()
      await consumeSSE<unknown>({
        url: `${getOntologyApiBaseUrl()}/events/stream`,
        body: { topics },
        signal: attemptController.signal,
        onEvent: (event) => {
          if (attemptController.signal.aborted) return
          armWatchdog()
          const frame = parseDomainEventFrame(event)
          if (frame.type === 'ready') {
            ready = true
            attempt = 0
            refreshedAfterUnauthorized = false
            onReady(frame.topics)
            onStatus('live')
          } else if (frame.type === 'signal') {
            onSignal(frame.topic)
          } else if (frame.type === 'unknown') {
            onResyncAll()
          }
        }
      })
    } catch (error) {
      failure = error
    } finally {
      clearTimeout(watchdog)
      signal.removeEventListener('abort', abortAttempt)
    }
    if (signal.aborted) return

    if (
      failure instanceof SSERequestError &&
      failure.status === 401 &&
      !refreshedAfterUnauthorized &&
      AuthenticationStorage.refreshToken
    ) {
      refreshedAfterUnauthorized = true
      const forced = await refreshAuthenticationToken(dispatch, { force: true })
      if (signal.aborted) return
      if (forced.ok) continue
    }
    if (isAuthFailure(failure)) {
      onStatus('off')
      return
    }
    // A clean close after `ready` is the server's lifetime rotation; the next
    // `ready` refetches. Anything else is a gap with no reopen to cover it yet.
    const failed = stalled || failure !== undefined || !ready
    if (failed) {
      onResyncAll()
      onStatus('degraded')
    } else {
      onStatus('connecting')
    }
    const delayMs = nextDomainEventsBackoffMs(attempt)
    if (failed) attempt += 1
    await abortableDelay(delayMs, signal)
  }
}

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const timer = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve()
      },
      { once: true }
    )
  })
}
